import React, { FC, useEffect, useState } from 'react';
import { Button, Col, Row, Tooltip } from 'antd';
import { DashboardCard } from 'common/components/DashboardCard';
import { Selector } from 'common/components/Selector/Selector';
import { dateFormatter } from 'common/helpers/date.helper';
import { InfoTooltip } from 'common/components/InfoTooltip';
import { PushPin, PushPinSlash } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getTrackStoreOrderDetails } from 'app/store/reducers/trackStore.reducer';
import {
  ETrackStoreOrderType,
  ETrackStoreOrderView,
  ITrackStoreLiveStore,
  ITrackStoreOnDuty,
  ITrackStoreOrderCollectionFilters,
  ITrackStoreOrderListItem,
  ITrackStoreStoreCard,
} from 'entities/TrackStore/TrackStore.models';
import { TrackStoreSummary } from 'entities/TrackStore/components/TrackStoreSummary';
import { TrackStoreWeeklyGrid } from 'entities/TrackStore/components/TrackStoreWeeklyGrid';
import { TrackStoreStoreOrders } from 'entities/TrackStore/components/TrackStoreStoreOrders';
import { TrackStoreOrderDetailsModal } from 'entities/TrackStore/components/TrackStoreOrderDetailsModal';
import { trackStoreTransport } from 'entities/TrackStore/TrackStore.transport';
import {
  STORE_CARD_ORDERS_HEIGHT,
  STORE_ORDERS_LIMIT,
  trackStoreOrderTypeOptions,
} from 'entities/TrackStore/TrackStore.const';

interface IComponentProps {
  card: ITrackStoreStoreCard;
  onDuty?: ITrackStoreOnDuty[];
  liveStore?: ITrackStoreLiveStore;
  pinned?: boolean;
  onTogglePin?: (storeCode: string) => void;
}

export const TrackStoreStoreCard: FC<IComponentProps> = ({
  card,
  onDuty = [],
  liveStore,
  pinned = false,
  onTogglePin,
}) => {
  const dispatch = useAppDispatch();
  const { trackStoreOrderDetails, trackStoreOrderDetailsLoading } = useAppSelector((state) => state.trackStore);
  const [orderType, setOrderType] = useState<ETrackStoreOrderType>(ETrackStoreOrderType.Reg);
  const [queueOrders, setQueueOrders] = useState<ITrackStoreOrderListItem[]>([]);
  const [servedOrders, setServedOrders] = useState<ITrackStoreOrderListItem[]>([]);
  const [ordersLoading, setOrdersLoading] = useState<boolean>(false);
  const [openTrackStoreOrderDetailsModal, setOpenTrackStoreOrderDetailsModal] = useState<boolean>(false);
  const [showOrders, setShowOrders] = useState<boolean>(false);

  useEffect(() => {
    if (!liveStore) {
      return;
    }

    setQueueOrders(liveStore.queueOrders);
    setServedOrders(liveStore.servedOrders);
  }, [liveStore]);

  const loading = ordersLoading || trackStoreOrderDetailsLoading;
  const queueMax = liveStore?.queueMax ?? 0;
  const servedOnTimePercent = liveStore?.servedOnTimePercent ?? null;

  const changeOrderType = async (value?: string | number): Promise<void> => {
    if (!value) {
      return;
    }

    const filters: ITrackStoreOrderCollectionFilters = {
      storeCode: card.storeCode,
      location: card.location,
      type: value as ETrackStoreOrderType,
      offset: 0,
      limit: STORE_ORDERS_LIMIT,
    };

    setOrderType(value as ETrackStoreOrderType);

    const [trackStoreQueueOrderCollection, trackStoreServedOrderCollection] = await Promise.all([
      await trackStoreTransport.getOrderCollection({ ...filters, view: ETrackStoreOrderView.Queue }),
      await trackStoreTransport.getOrderCollection({ ...filters, view: ETrackStoreOrderView.Served }),
    ]);

    setQueueOrders(trackStoreQueueOrderCollection.data);
    setServedOrders(trackStoreServedOrderCollection.data);
  };

  const handleLoadMore = async (
    params: Omit<ITrackStoreOrderCollectionFilters, 'storeCode' | 'location'>
  ): Promise<void> => {
    const filters: ITrackStoreOrderCollectionFilters = {
      ...params,
      storeCode: card.storeCode,
      location: card.location,
      type: orderType,
    };

    setOrdersLoading(true);

    const { data } = await trackStoreTransport.getOrderCollection(filters);

    if (filters.view === ETrackStoreOrderView.Queue) {
      setQueueOrders((prev) => [...prev, ...data]);
    } else {
      setServedOrders((prev) => [...prev, ...data]);
    }

    setOrdersLoading(false);
  };

  const toggleShowOrders = (): void => setShowOrders((prev) => !prev);

  const openOrderDetailsModal = async (transactionId: string): Promise<void> => {
    await dispatch(getTrackStoreOrderDetails({ transactionId }));
    setOpenTrackStoreOrderDetailsModal(true);
  };

  const closeOrderDetailsModal = () => {
    setOpenTrackStoreOrderDetailsModal(false);
  };

  const togglePin = (): void => onTogglePin?.(card.storeCode);

  return (
    <div>
      <DashboardCard
        title={card.storeCode}
        variant="store"
        extra={
          <Tooltip title={pinned ? 'Unpin store' : 'Pin store'}>
            <Button
              className={pinned ? 'tsrt-store-card-pin tsrt-store-card-pin--active' : 'tsrt-store-card-pin'}
              icon={pinned ? <PushPinSlash /> : <PushPin />}
              onClick={togglePin}
            />
          </Tooltip>
        }
      >
        <div className="tsrt-store-card-on-duty">
          <div className="mb-3 ff-regular fs-xxxs color-gray-1">On Duty</div>

          <Row gutter={10}>
            {onDuty.map((i, index) => (
              <Col key={index} span={11}>
                <span className="ff-regular fs-xxs word-break">{`${i.crewName}`}</span>
                <span className="ff-regular fs-xxs">{` - ${dateFormatter(i.firstSeenAt, 'h:mm a')}`}</span>
              </Col>
            ))}
          </Row>
        </div>

        <div className="pt-5 pr-7 pb-5 pl-7">
          <div className="mb-7">
            <TrackStoreSummary summary={card.summary} />
          </div>

          {liveStore && showOrders && (
            <div className="mb-7">
              <Row className="mb-5" align="middle" justify="space-between">
                <Col>
                  <Selector
                    placeholder="Select order type"
                    allowClear={false}
                    options={trackStoreOrderTypeOptions}
                    value={orderType}
                    onChange={changeOrderType}
                  />
                </Col>
                <Col>
                  <InfoTooltip
                    title={
                      <div>
                        <div className="mb-4 ff-medium fs-xxxs color-gray-1">See active orders at a glance!</div>
                        <Row className="mb-4">
                          <Col span={5} className="ff-regular fs-xxxs color-green">
                            Green
                          </Col>
                          <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                            =
                          </Col>
                          <Col span={17} className="ff-regular fs-xxxs">
                            on target
                          </Col>
                        </Row>
                        <Row className="mb-4">
                          <Col span={5} className="ff-regular fs-xxxs color-red">
                            Red
                          </Col>
                          <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                            =
                          </Col>
                          <Col span={17} className="ff-regular fs-xxxs">
                            delayed
                          </Col>
                        </Row>
                        <div className="mb-4 ff-medium fs-xxxs color-gray-1">Track your live orders:</div>
                        <Row className="mb-4">
                          <Col span={5} className="ff-regular fs-xxxs">
                            Queue
                          </Col>
                          <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                            =
                          </Col>
                          <Col span={17} className="ff-regular fs-xxxs">
                            orders waiting to be served
                          </Col>
                        </Row>
                        <Row className="mb-4">
                          <Col span={5} className="ff-regular fs-xxxs">
                            Served
                          </Col>
                          <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                            =
                          </Col>
                          <Col span={17} className="ff-regular fs-xxxs">
                            orders already done
                          </Col>
                        </Row>
                        <div className="ff-medium fs-xxxs color-gray-1">
                          Click the dropdown to switch between Regular & Advanced orders.
                        </div>
                      </div>
                    }
                  />
                </Col>
              </Row>

              <Row>
                <Col span={12}>
                  <div className="mb-4">
                    <span className="ff-regular fs-xxs">Queue</span>
                    <span className="color-gray-1"> • </span>
                    <span className="ff-medium fs-xxs color-purple">{`${queueMax} max/hr`}</span>
                  </div>
                  <div style={{ height: STORE_CARD_ORDERS_HEIGHT }}>
                    <TrackStoreStoreOrders
                      view={ETrackStoreOrderView.Queue}
                      orders={queueOrders}
                      loading={loading}
                      onLoadMore={handleLoadMore}
                      onOpenOrder={openOrderDetailsModal}
                    />
                  </div>
                </Col>
                <Col span={12}>
                  <div className="mb-4">
                    <span className="ff-regular fs-xxs">Served</span>
                    <span className="color-gray-1"> • </span>
                    <span className="ff-medium fs-xxs color-purple">
                      {servedOnTimePercent !== null ? `${servedOnTimePercent}% on time` : '- on time'}
                    </span>
                  </div>
                  <div style={{ height: STORE_CARD_ORDERS_HEIGHT }}>
                    <TrackStoreStoreOrders
                      view={ETrackStoreOrderView.Served}
                      orders={servedOrders}
                      loading={loading}
                      onLoadMore={handleLoadMore}
                      onOpenOrder={openOrderDetailsModal}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          )}

          {!!(queueOrders.length || servedOrders.length) && (
            <div className="mb-7">
              <Button className="btn-secondary width-full" onClick={toggleShowOrders}>
                {showOrders ? 'Hide Orders' : 'Show Orders'}
              </Button>
            </div>
          )}

          <TrackStoreWeeklyGrid weeklyGrid={card.weeklyGrid} />
        </div>
      </DashboardCard>

      <TrackStoreOrderDetailsModal
        open={openTrackStoreOrderDetailsModal}
        orderDetails={trackStoreOrderDetails}
        onCancel={closeOrderDetailsModal}
      />
    </div>
  );
};
